import React, { useEffect, useRef, useState } from 'react'
import { Phone, PhoneOff, MessageSquare } from 'lucide-react'
import FocusTrap from 'focus-trap-react'
import type { CallerInfo } from '@/stores/callStore'
import { SafeAreaInsets } from '@/components/ui/SafeAreaInsets'
import { SafeAreaBottom } from '@/components/ui/SafeAreaBottom'
import { AvatarStateRing } from './AvatarStateRing'

interface MobileIncomingCallOverlayProps {
  callerInfo: CallerInfo | null
  onAccept: () => void
  onDecline: () => void
  onMessage?: () => void
}

/**
 * Full-screen incoming-call sheet. Direction B layout:
 *  - centered AvatarStateRing (white ringing pulse — an incoming call needs you now)
 *  - `.date-hero` caller name
 *  - full-width Decline + Accept buttons anchored to the safe-area bottom
 *
 * iOS safe-area: the bottom button group is wrapped in SafeAreaBottom so
 * the home indicator never overlaps the call-control hit area
 * (CLAUDE.md §8; FOR-1544/1584/1597).
 */
export function MobileIncomingCallOverlay({
  callerInfo,
  onAccept,
  onDecline,
  onMessage,
}: MobileIncomingCallOverlayProps) {
  const acceptBtnRef = useRef<HTMLButtonElement>(null)
  const vibrateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [declineFocused, setDeclineFocused] = useState(false)
  const [acceptFocused, setAcceptFocused] = useState(false)

  useEffect(() => {
    acceptBtnRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onDecline])

  // navigator.vibrate — feature-detect, repeat until unmount
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return
    const pattern = [200, 100, 200, 100, 200]
    navigator.vibrate(pattern)
    vibrateIntervalRef.current = setInterval(() => {
      navigator.vibrate(pattern)
    }, 2000)
    return () => {
      if (vibrateIntervalRef.current) clearInterval(vibrateIntervalRef.current)
      navigator.vibrate(0)
    }
  }, [])

  return (
    <FocusTrap focusTrapOptions={{ returnFocusOnDeactivate: true }}>
      <SafeAreaInsets
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-caller-name"
        aria-describedby="mobile-call-status"
        // 100dvh keeps the bottom buttons inside the visible viewport when iOS
        // Safari's URL bar collapses (FND-M-016).
        className="md:hidden fixed inset-0 z-[9999] flex flex-col items-stretch justify-between animate-fade-up"
        style={{ background: 'var(--midnight-bg)', height: '100dvh' }}
        minTop={32}
        minBottom={0}
        minLeft={20}
        minRight={20}
      >
        {/* Backdrop blur sheen — soft white wash that sells "system event" */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at top, rgba(255,255,255,0.08) 0%, rgba(0,0,0,0) 60%)',
            pointerEvents: 'none',
          }}
        />

        {/* ── Hero column: avatar + caller name ────────────────────────── */}
        <div
          className="relative z-10 flex flex-col items-center"
          style={{ gap: 24, paddingTop: 8 }}
        >
          <p
            id="mobile-call-status"
            className="eyebrow"
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--midnight-text3)',
            }}
          >
            Incoming call
          </p>

          <AvatarStateRing
            name={callerInfo?.name ?? undefined}
            state="ringing"
            size="lg"
            pulse
          />

          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              id="mobile-caller-name"
              className="date-hero"
              style={{
                color: '#FFFFFF',
                fontSize: 'clamp(2rem, 7vw, 3.25rem)',
                lineHeight: 1.05,
                padding: '0 16px',
                overflowWrap: 'anywhere',
              }}
            >
              {callerInfo?.name || 'Unknown caller'}
            </span>
            {callerInfo?.number && (
              <span
                style={{
                  fontSize: 15,
                  color: 'rgba(255,255,255,0.60)',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.02em',
                }}
              >
                {callerInfo.number}
              </span>
            )}
          </div>
        </div>

        {/* ── Optional quick-message pill ──────────────────────────────── */}
        {onMessage && (
          <div className="relative z-10 flex justify-center" style={{ paddingBottom: 8 }}>
            <button
              type="button"
              onClick={onMessage}
              aria-label="Send quick message instead of answering"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 40,
                padding: '0 18px',
                borderRadius: 'var(--r-pill, 9999px)',
                border: '0.5px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.75)',
                // 16px floor — iOS Safari auto-zooms <16px on focused inputs.
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                touchAction: 'manipulation',
              }}
            >
              <MessageSquare style={{ width: 16, height: 16 }} />
              Message
            </button>
          </div>
        )}

        {/* ── Bottom: full-width Decline + Accept (safe-area aware) ────── */}
        <SafeAreaBottom
          extra={16}
          className="relative z-10 w-full"
          style={{ paddingLeft: 20, paddingRight: 20 }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
            }}
          >
            <button
              type="button"
              onClick={onDecline}
              aria-label="Decline call"
              onFocus={() => setDeclineFocused(true)}
              onBlur={() => setDeclineFocused(false)}
              className="btn-danger"
              style={{
                height: 64,
                borderRadius: '9999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontSize: 16,
                fontWeight: 600,
                touchAction: 'manipulation',
                outline: declineFocused ? '2px solid rgba(255,255,255,0.8)' : 'none',
                outlineOffset: 3,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <PhoneOff style={{ width: 22, height: 22 }} />
              Decline
            </button>
            <button
              ref={acceptBtnRef}
              type="button"
              onClick={onAccept}
              aria-label="Accept call"
              onFocus={() => setAcceptFocused(true)}
              onBlur={() => setAcceptFocused(false)}
              className="btn-primary"
              style={{
                height: 64,
                borderRadius: '9999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontSize: 16,
                fontWeight: 600,
                touchAction: 'manipulation',
                outline: acceptFocused ? '2px solid rgba(255,255,255,0.8)' : 'none',
                outlineOffset: 3,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Phone style={{ width: 22, height: 22 }} />
              Accept
            </button>
          </div>
        </SafeAreaBottom>
      </SafeAreaInsets>
    </FocusTrap>
  )
}
